import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import logo from '../assets/logo-villa-thailand.jpg';

// Flag SVG components
const GermanFlag = () => (
  <svg className="w-6 h-4 rounded-sm shadow-sm" viewBox="0 0 5 3">
    <rect width="5" height="1" y="0" fill="#000"/>
    <rect width="5" height="1" y="1" fill="#DD0000"/>
    <rect width="5" height="1" y="2" fill="#FFCE00"/>
  </svg>
);

const USAFlag = () => (
  <svg className="w-6 h-4 rounded-sm shadow-sm" viewBox="0 0 190 100">
    <rect width="190" height="100" fill="#bf0a30"/>
    <rect y="7.69" width="190" height="7.69" fill="#fff"/>
    <rect y="23.08" width="190" height="7.69" fill="#fff"/>
    <rect y="38.46" width="190" height="7.69" fill="#fff"/>
    <rect y="53.85" width="190" height="7.69" fill="#fff"/>
    <rect y="69.23" width="190" height="7.69" fill="#fff"/>
    <rect y="84.62" width="190" height="7.69" fill="#fff"/>
    <rect width="76" height="53.85" fill="#002868"/>
  </svg>
);

const ThaiFlag = () => (
  <svg className="w-6 h-4 rounded-sm shadow-sm" viewBox="0 0 900 600">
    <rect width="900" height="600" fill="#A51931"/>
    <rect y="100" width="900" height="400" fill="#F4F5F8"/>
    <rect y="200" width="900" height="200" fill="#2D2A4A"/>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, changeLanguage, t, getRoutes } = useLanguage();

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const routes = getRoutes();

  const navItems = [
    { path: routes.home, label: t('nav.home') },
    { path: routes.villa, label: t('nav.villa') },
    { path: routes.area, label: t('nav.area') },
    { path: routes.arrival, label: t('nav.arrival') },
    { path: routes.conditions, label: t('nav.conditions') },
    { path: routes.contact, label: t('nav.contact') },
    { path: routes.booking, label: t('nav.booking'), highlight: true },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-all duration-300">
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={routes.home}>
              <img 
                src={logo} 
                alt="Villa Thailand Logo" 
                className="h-12 md:h-16 w-auto"
              />
            </Link>
            
            {/* Language Flags */}
            <div className="flex items-center gap-2 ml-2">
              <button 
                onClick={() => changeLanguage('de')}
                className={`hover:opacity-70 transition-opacity p-1 border-2 rounded ${
                  language === 'de' ? 'border-amber-500' : 'border-transparent hover:border-gray-300'
                }`}
                title="Deutsch"
              >
                <GermanFlag />
              </button>
              <button 
                onClick={() => changeLanguage('en')}
                className={`hover:opacity-70 transition-opacity p-1 border-2 rounded ${
                  language === 'en' ? 'border-amber-500' : 'border-transparent hover:border-gray-300'
                }`}
                title="English"
              >
                <USAFlag />
              </button>
              <button 
                onClick={() => changeLanguage('th')}
                className={`hover:opacity-70 transition-opacity p-1 border-2 rounded ${
                  language === 'th' ? 'border-amber-500' : 'border-transparent hover:border-gray-300'
                }`}
                title="ภาษาไทย"
              >
                <ThaiFlag />
              </button>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className={`transition-colors duration-300 font-medium text-sm lg:text-base ${
                  item.highlight 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 shadow-md' 
                    : 'text-gray-700 hover:text-amber-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-gray-700 hover:text-amber-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path} 
                  onClick={closeMobileMenu} 
                  className={`transition-colors duration-300 font-medium py-2 ${
                    item.highlight 
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 rounded-lg text-center' 
                      : 'text-gray-700 hover:text-amber-600'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
